import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { Menubar } from 'primeng/menubar';
import { FeedbackConfig, FeedbackDialogComponent, FeedbackSubmission } from '@brighthub/feedback';
import { MenuItem, MessageService } from 'primeng/api';
import { FeedbackService } from '@brighthub/feedback';
import { Toast } from 'primeng/toast';

interface ChatMessageFeedbackInterface {
  titleText: string;
  preSelectedTag: string;
  suggestedTags: string[];
  commentText: string;
}

export const CHAT_FEEDBACK_PRESETS: Record<string, ChatMessageFeedbackInterface> = {
  'positive-experience': {
    titleText: 'Glad that helped! How would you rate this experience?',
    preSelectedTag: 'Positive Experience',
    suggestedTags: ['Helpful', 'Clear', 'Friendly'],
    commentText: 'Tell us what worked well for you. (Optional)',
  },
  'negative-experience': {
    titleText: 'Sorry about that! How would you rate this experience?',
    preSelectedTag: 'Negative Experience',
    suggestedTags: ['Confusing', 'Wrong', 'Too Slow'],
    commentText: 'What could we do better? (Optional)',
  },
  'helpful': {
    titleText: 'Awesome! Thanks for letting us know.',
    preSelectedTag: 'Helpful',
    suggestedTags: ['Accurate', 'Appreciated'],
    commentText: 'Want to share what made this helpful? (Optional)',
  },
  'confusing': {
    titleText: 'We hear you—it was confusing.',
    preSelectedTag: 'Confusing',
    suggestedTags: ['Hard to follow', 'Missing detail', 'Too repetitive'],
    commentText: 'What part was unclear? (Optional)',
  },
  'wrong': {
    titleText: 'Thanks—we’ll work on improving accuracy.',
    preSelectedTag: 'Wrong',
    suggestedTags: ['Incorrect info', 'Off-topic', 'Didn’t work'],
    commentText: 'What went wrong? (Optional)',
  }
};


@Component({
  selector: 'lib-chat-message-feedback',
  imports: [CommonModule, Button, Menubar, FeedbackDialogComponent],
  templateUrl: './chat-message-feedback.component.html',
  styleUrl: './chat-message-feedback.component.scss',
})
export class ChatMessageFeedbackComponent {
  @Input() messageId!: string;
  @Input() conversationId!: string;
  hovered: 'up' | 'down' | null = null;
  showFeedbackDialog = false;
  feedbackProps: FeedbackConfig | null = null;
  private _messageService = inject(MessageService);
  private _feedbackService = inject(FeedbackService);
  private _cdRef = inject(ChangeDetectorRef);
  toastTimeoutId: any = null;
  pendingQuickFeedback: FeedbackConfig | null = null;

  menuItems: MenuItem[] = [
    {
      label: 'Quick Feedback',
      icon: 'pi pi-comment',
      items: [
        {
          label: 'Helpful',
          icon: 'pi pi-check',
          command: () => this.quickFeedback('helpful'),
        },
        {
          label: 'Confusing',
          icon: 'pi pi-question',
          command: () => this.quickFeedback('confusing'),
        },
        {
          label: 'Wrong',
          icon: 'pi pi-times',
          command: () => this.quickFeedback('wrong'),
        },
      ],
    },
  ];

  closeFeedback(): void {
    this.showFeedbackDialog = false;
    this.feedbackProps = null;
    this.pendingQuickFeedback = null;
  }

  openFeedback(mode: string): void {
    const config = this.getInfoForFeedback(mode.toLowerCase());
    this.feedbackProps = {
      scope: { application: 'brightAI', component: 'chat-message' },
      metadata: {
        messageId: this.messageId,
        conversationId: this.conversationId,
      },
      userTags: [config.preSelectedTag],
      suggestedTags: config.suggestedTags,
      titleText: config.titleText,
      commentText: config.commentText,
    };
    this.showFeedbackDialog = true;
    this._cdRef.detectChanges();
  }

  getInfoForFeedback(feedbackId: string): ChatMessageFeedbackInterface {
    return CHAT_FEEDBACK_PRESETS[feedbackId];
  }

  quickFeedback(mode: string): void {
    const config = this.getInfoForFeedback(mode.toLowerCase());

    this.pendingQuickFeedback = {
      scope: { application: 'brightAI', component: 'chat-message' },
      metadata: {
        messageId: this.messageId,
        conversationId: this.conversationId,
      },
      userTags: [config.preSelectedTag],
      suggestedTags: config.suggestedTags,
      titleText: config.titleText,
      commentText: config.commentText,
    };

    this._messageService.clear('has-actions');
    this._messageService.add({
      key: 'has-actions',
      severity: 'info',
      summary: 'Thanks! Want to tell us more?',
      sticky: true,
      data: {
        actions: [
          {
            label: 'Expand',
            onClick: () => {
              this.onExpandFeedback(mode.toLowerCase());
            },
            icon: 'pi pi-external-link',
            severity: 'info',
            size: 'small',
          },
          {
            label: 'Dismiss',
            onClick: () => this.onToastClosed(),
            icon: 'pi pi-times',
            severity: 'secondary',
            size: 'small',
          },
        ],
      },
    });

  }

  onFeedbackCanceled(): void {
    this.onToastClosed();
  }

  onExpandFeedback(mode: string): void {
    clearTimeout(this.toastTimeoutId);
    this._messageService.clear('has-actions');

    if (this.pendingQuickFeedback) {
      this.openFeedback(mode);
    }
  }

  onToastClosed(): void {
    if (this.pendingQuickFeedback) {
      this._feedbackService
        .submitFeedback(this.pendingQuickFeedback)
        .subscribe();
      this.pendingQuickFeedback = null;
    }
    this.closeFeedback();
  }
}
